import React, { useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import SendIcon from '@mui/icons-material/Send';
import TextField from '@mui/material/TextField';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import '../assets/styles/Contact.scss';
import { PortfolioContent } from '../data/content';

const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mljdvyoq';

interface ContactProps {
  content: PortfolioContent['contact'];
  social: PortfolioContent['main']['social'];
}

function Contact({ content, social }: ContactProps) {
  const [name, setName] = useState<string>('');
  const [contactInfo, setContactInfo] = useState<string>('');
  const [message, setMessage] = useState<string>('');

  const [nameError, setNameError] = useState<boolean>(false);
  const [contactError, setContactError] = useState<boolean>(false);
  const [messageError, setMessageError] = useState<boolean>(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [loading, setLoading] = useState<boolean>(false);

  const formRef = useRef<HTMLFormElement>(null);

  const clearSubmitFeedback = () => {
    if (submitStatus !== 'idle') setSubmitStatus('idle');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const isNameInvalid = name.trim() === '';
    const isContactInvalid = contactInfo.trim() === '';
    const isMessageInvalid = message.trim() === '';

    setNameError(isNameInvalid);
    setContactError(isContactInvalid);
    setMessageError(isMessageInvalid);

    if (isNameInvalid || isContactInvalid || isMessageInvalid) {
      return;
    }

    const trimmedContact = contactInfo.trim();
    const payload: Record<string, string> = {
      name: name.trim(),
      message: message.trim(),
      contact: trimmedContact,
    };
    if (trimmedContact.includes('@')) {
      payload.email = trimmedContact;
      payload._replyto = trimmedContact;
    }

    setLoading(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setName('');
        setContactInfo('');
        setMessage('');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="contact" style={{ backgroundColor: '#FFFFFF', width: '100%' }}>
      <div className="items-container">
        <div className="contact_wrapper">
          <h1>{content.heading}</h1>
          <p className="contact-intro">{content.subheading}</p>

          <Box
            ref={formRef}
            component="form"
            noValidate
            autoComplete="off"
            className="contact-form"
            onSubmit={handleSubmit}
          >
            <div className="form-flex">
              <TextField
                required
                id="contact-name"
                label={content.nameLabel}
                placeholder={content.namePlaceholder}
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (nameError) setNameError(false);
                  clearSubmitFeedback();
                }}
                error={nameError}
                helperText={nameError ? content.nameError : ""}
              />
              <TextField
                required
                id="contact-info"
                label={content.contactLabel}
                placeholder={content.contactPlaceholder}
                value={contactInfo}
                onChange={(e) => {
                  setContactInfo(e.target.value);
                  if (contactError) setContactError(false);
                  clearSubmitFeedback();
                }}
                error={contactError}
                helperText={contactError ? content.contactError : ""}
              />
            </div>
            <TextField
              required
              id="contact-message"
              label={content.messageLabel}
              placeholder={content.messagePlaceholder}
              multiline
              rows={6}
              className="body-form"
              value={message}
              onChange={(e) => {
                setMessage(e.target.value);
                if (messageError) setMessageError(false);
                clearSubmitFeedback();
              }}
              error={messageError}
              helperText={messageError ? content.messageError : ""}
            />

            {submitStatus === 'success' && (
              <p style={{ color: '#2B2B2B', fontWeight: 600, margin: '8px 0' }}>
                {content.successMessage}
              </p>
            )}
            {submitStatus === 'error' && (
              <p style={{ color: '#2B2B2B', fontWeight: 600, margin: '8px 0' }}>
                {content.errorMessage}
              </p>
            )}

            <Button
              type="submit"
              variant="contained"
              endIcon={<SendIcon />}
              disabled={loading}
            >
              {content.sendButton}
            </Button>
          </Box>

          <div className="direct-contact-info">
            <p>{content.directContact}</p>
            <div className="direct-links">
              {social.email && (
                <a
                  href={`mailto:${social.email}`}
                  title={`Email: ${social.email}`}
                >
                  <EmailIcon fontSize="small" />
                  <span>Email: {social.email}</span>
                </a>
              )}
              {social.linkedin && (
                <a
                  href={social.linkedin}
                  target="_blank"
                  rel="noreferrer"
                  title={`LinkedIn: ${social.linkedin}`}
                >
                  <LinkedInIcon fontSize="small" />
                  <span>LinkedIn: {social.linkedin}</span>
                </a>
              )}
              {social.github && (
                <a
                  href={social.github}
                  target="_blank"
                  rel="noreferrer"
                  title={`GitHub: ${social.github}`}
                >
                  <GitHubIcon fontSize="small" />
                  <span>GitHub: {social.github}</span>
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Contact;